import { db } from "../firebase-config";
import { collection, doc, setDoc, query, onSnapshot, deleteDoc, updateDoc, serverTimestamp, deleteField } from "firebase/firestore";

// Snapshot listener for all departments
export const departmentsSnapshotListener = (organization, onDepartmentUpdate) => {
    const departmentQuery = query(collection(db, "organizations", organization, "departments"))
    const unsubscribe = onSnapshot(departmentQuery, (querySnapshot) => {
        const departmentItems = [];
        querySnapshot.forEach((doc) => {
            departmentItems.push({id: doc.id, ...doc.data()})
        });
        onDepartmentUpdate(departmentItems);
    })
    return () => unsubscribe()
}

// Snapshot listener for a single department
export const departmentSnapshotListener = (organization, id, onDepartmentUpdate) => {
    const departmentDocRef = doc(db, "organizations", organization, "departments", id);

    const unsubscribe = onSnapshot(departmentDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
            onDepartmentUpdate({ id: docSnapshot.id, ...docSnapshot.data() });
        } else {
            onDepartmentUpdate(null);
        }
    });
    return unsubscribe;
};

// Add a new department
export const addDepartment = async (organization, departmentName) => {
    try {
        const departmentRef = doc(db, "organizations", organization, "departments", departmentName);
        await setDoc(departmentRef, {
            name: departmentName,
            employees: [],
            created_at: serverTimestamp(),
            updated_at: serverTimestamp()
        });
        return { success: true, id: departmentRef.id, error: null };
    } catch (error) {
        console.error("Error adding department:", error);
        return { success: false, error: error.message || 'Failed to add department' };
    }
};

export const deleteDepartment = async (organization, departmentId) => {
    try {
        const departmentRef = doc(db, "organizations", organization, "departments", departmentId);
        await deleteDoc(departmentRef);
        return { success: true, error: null };
    } catch (error) {
        console.error("Error deleting department:", error);
        return { success: false, error: error.message || 'Failed to delete department' };
    }
};

// Update department details including setting managers
export const updateDepartmentManagers = async (organization, departmentId, employeeId, isManager) => {
    try {
        const departmentRef = doc(db, "organizations", organization, "departments", departmentId);
        
        // Update the specific employee's manager status
        await updateDoc(departmentRef, {
            [`employees.${employeeId}.is_manager`]: isManager,
            updated_at: serverTimestamp()
        });
        
        return { success: true, error: null };
    } catch (error) {
        console.error("Error updating department managers:", error);
        return { success: false, error: error.message || 'Failed to update department managers' };
    }
};

export const addEmployeeToDepartment = async (organization, oldDepartmentId, newDepartmentId, employeeId) => {
    try {
        const departmentRef = doc(db, "organizations", organization, "departments", newDepartmentId);
        await updateDoc(departmentRef, {
            [`employees.${employeeId}`]: { 
            is_manager: false 
        }    
        }, { merge: true });

        const oldDepartmentRef = doc(db, "organizations", organization, "departments", oldDepartmentId);
        await updateDoc(oldDepartmentRef, {
            [`employees.${employeeId}`]: deleteField()
        }, { merge: true });

        return { success: true, error: null };
    } catch (error) {
        console.error("Error adding employee to department:", error);
        return { success: false, error: error.message || 'Failed to add employee to department' };
    }
}

// Set a department manager
export const setDepartmentManager = async (organization, departmentId, employeeId) => {
    try {
        const departmentRef = doc(db, "organizations", organization, "departments", departmentId);
        await updateDoc(departmentRef, {
            manager_id: employeeId,
            updated_at: serverTimestamp()
        });
        return { success: true, error: null };
    } catch (error) {
        console.error("Error setting department manager:", error);
        return { success: false, error: error.message || 'Failed to set department manager' };
    }
};

// Remove an employee from a department's employees map
export const removeEmployeeFromDepartment = async (organization, departmentId, employeeId) => {
    try {
        const departmentRef = doc(db, "organizations", organization, "departments", departmentId);
        await updateDoc(departmentRef, {
            [`employees.${employeeId}`]: deleteField()
        });
        return { success: true, error: null };
    } catch (error) {
        console.error("Error removing employee from department:", error);
        return { success: false, error: error.message || 'Failed to remove employee from department' };
    }
};
